/**
 * LocationTemplate — Morgan Legal Group
 * Data-driven landing page for a single office / service-area, in "The Counsel"
 * style and fully optimized for Google Business Profile (LocalBusiness/LegalService
 * JSON-LD with NAP, hours, sameAs to the GBP, areaServed — без aggregateRating).
 */
import { useEffect } from 'react';
import { Phone, Calendar, MapPin, Clock, Star, Check, ArrowRight, ExternalLink } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import CalendarEmbed from '@/components/CalendarEmbed';
import type { LocationData } from '@/data/locations';
import { applyPageMeta } from '@/lib/pageMeta';

const SITE = 'https://www.morganlegalny.com';

const PRACTICE_LINKS = [
  { label: 'Estate Planning', href: '/estate-planning/' },
  { label: 'Probate', href: '/probate/' },
  { label: 'Wills & Trusts', href: '/trusts/' },
  { label: 'Elder Law', href: '/elder-law/' },
  { label: 'Asset Protection', href: '/asset-protection/' },
  { label: 'Guardianship', href: '/power-of-attorney/' },
  { label: 'Power of Attorney', href: '/power-of-attorney/' },
  { label: 'Family Law', href: '/family-law/' },
];

/**
 * 🔴 «Open 24 hours» само по себе неверно и подтверждено владельцем 03.09:
 * круглосуточно телефон поднимают ОПЕРАТОРЫ, а встреча с юристом — Пн–Пт 9–18.
 * Прежняя строка обещала посетителю адвоката в три часа ночи, а разметка
 * `openingHoursSpecification` утверждала это же машине. Пишем обе величины
 * раздельно: приём звонков и приём у юриста — разные вещи.
 */
function hoursLabel(h: '24/7' | 'mf9-6') {
  return h === '24/7'
    ? 'Calls answered 24/7 · Attorney meetings Mon–Fri 9:00 AM – 6:00 PM'
    : 'Mon–Fri: 9:00 AM – 6:00 PM';
}

function openingHoursSchema(h: '24/7' | 'mf9-6') {
  return h === '24/7'
    ? [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '00:00', closes: '23:59' }]
    : [{ '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' }];
}

export default function LocationTemplate({ location: loc }: { location: LocationData }) {
  const canonical = `${SITE}${loc.url}`;
  // Location titles run long ("Estate Planning & Probate Attorney in Westchester
  // County"), so the brand is abbreviated to keep the keyword phrase inside the
  // width Google renders.
  const metaTitle = `${loc.title} | Morgan Legal`;
  const metaDescription = loc.heroIntro.slice(0, 155).replace(/\s+\S*$/, '') + '…';
  const office = loc.office;
  const phone = office?.phone ?? '888-529-1315';
  const phoneHref = office?.phoneHref ?? '8885291315';
  const gmbUrl = office?.gmbUrl ?? loc.gmbUrl;

  const mapQuery = office
    ? `${office.gmbName}, ${office.street}, ${office.city}, ${office.state} ${office.zip}`
    : `${loc.shortName}`;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;

  // ---- JSON-LD ----
  const businessSchema = office
    ? {
        '@context': 'https://schema.org',
        '@type': 'LegalService',
        '@id': `${canonical}#business`,
        name: `Morgan Legal Group — ${loc.shortName}`,
        url: canonical,
        telephone: office.phone,
        priceRange: '$$',
        image: `${SITE}/brand/morgan-logo.png`,
        address: {
          '@type': 'PostalAddress',
          streetAddress: office.street,
          addressLocality: office.city,
          addressRegion: office.state,
          postalCode: office.zip,
          addressCountry: 'US',
        },
        areaServed: loc.areasServed.map((a) => ({ '@type': 'Place', name: a })),
        openingHoursSpecification: openingHoursSchema(office.hours),
        // aggregateRating снят: см. комментарий в Home.tsx. Рейтинг и число
        // отзывов остаются видимым текстом на странице и ссылкой на профиль
        // Google — это утверждение, а не разметка о самом себе.
        sameAs: [office.gmbUrl],
        parentOrganization: { '@type': 'LegalService', name: 'Morgan Legal Group, P.C.', url: SITE },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'Estate planning, probate, and elder law',
        name: loc.title,
        url: canonical,
        areaServed: loc.areasServed.map((a) => ({ '@type': 'Place', name: a })),
        provider: {
          '@type': 'LegalService',
          name: 'Morgan Legal Group, P.C.',
          url: SITE,
          // Entity-level number: the firm's confirmed main line, matching the
          // Organization schema, the footer and Google Business Profile. The
          // per-location display numbers in locations.ts are separate.
          telephone: '(888) 529-1315',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '15 Maiden Ln #905',
            addressLocality: 'New York',
            addressRegion: 'NY',
            postalCode: '10038',
            addressCountry: 'US',
          },
        },
      };

  const schema = [
    businessSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Locations', item: `${SITE}/location/` },
        { '@type': 'ListItem', position: 3, name: loc.shortName, item: canonical },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: loc.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  useEffect(() => {
    if (typeof document !== 'undefined') {
      applyPageMeta({ title: metaTitle, description: metaDescription, url: canonical });
    }
  }, [metaTitle, metaDescription, canonical]);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={schema} />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-navy pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
            <span className="absolute bottom-0 -right-6 font-display text-[9rem] lg:text-[15rem] font-bold text-white leading-none whitespace-nowrap" style={{ opacity: 0.03 }}>
              {loc.shortName.toUpperCase()}
            </span>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="Breadcrumb" className="mb-6 font-body text-xs text-white/50 tracking-wide">
              <a href="/" className="hover:text-gold transition-colors">Home</a>
              <span className="mx-2 text-gold/60">/</span>
              <a href="/location/" className="hover:text-gold transition-colors">Locations</a>
              <span className="mx-2 text-gold/60">/</span>
              <span className="text-white/80">{loc.shortName}</span>
            </nav>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7">
                <div className="section-label mb-5 text-gold">
                  <span className="inline-block w-10 h-0.5 bg-gold align-middle mr-2" />
                  {office ? 'Local Office' : 'Service Area'} · New York Estate Law
                </div>
                <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold leading-[1.08] mb-6">
                  {loc.title}
                </h1>
                <p className="font-body text-white/80 text-base lg:text-lg leading-relaxed mb-8">{loc.heroIntro}</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="#book" className="btn-gold">
                    <Calendar className="w-4 h-4" />
                    Schedule Your Free Consultation
                  </a>
                  <a href={`tel:${phoneHref}`} className="btn-ghost-gold">
                    <Phone className="w-4 h-4" />
                    {phone}
                  </a>
                </div>
              </div>

              {/* Office / GMB card */}
              <div className="lg:col-span-5">
                <div className="bg-white/[0.04] border border-gold/30 backdrop-blur-sm p-6 lg:p-7">
                  {office && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} className="w-4 h-4 fill-gold text-gold" />
                        ))}
                      </div>
                      <span className="font-body text-white/90 text-sm font-semibold">{office.rating.toFixed(1)}</span>
                      <span className="font-body text-white/50 text-sm">· {office.reviewCount} Google reviews</span>
                    </div>
                  )}
                  <h2 className="font-display text-white text-lg font-bold mb-4">
                    {office ? `Morgan Legal Group — ${loc.shortName}` : `Serving ${loc.shortName}`}
                  </h2>
                  <ul className="space-y-3 font-body text-sm text-white/80">
                    {office ? (
                      <>
                        <li className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                          <span>{office.street}<br />{office.city}, {office.state} {office.zip}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                          <a href={`tel:${phoneHref}`} className="hover:text-gold transition-colors">{phone}</a>
                        </li>
                        <li className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-gold flex-shrink-0" />
                          <span>{hoursLabel(office.hours)}</span>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                          <span>Service area served from our New York City headquarters and regional offices.</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                          <a href={`tel:${phoneHref}`} className="hover:text-gold transition-colors">{phone}</a>
                        </li>
                      </>
                    )}
                  </ul>
                  {gmbUrl && (
                    <a
                      href={gmbUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-2 text-gold hover:text-gold-light font-body text-sm font-semibold transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View us on Google &amp; read reviews
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Local context */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-5">
            <div className="section-label mb-2">Estate &amp; Probate Counsel in {loc.shortName}</div>
            {loc.localContext.map((para, i) => (
              <p key={i} className="font-body text-navy/75 text-base lg:text-lg leading-relaxed">{para}</p>
            ))}
            <p className="font-body text-navy/70 text-sm leading-relaxed pt-2 border-t border-navy/10">
              <strong className="text-navy">{loc.surrogatesCourt}</strong>
            </p>
          </div>
        </section>

        {/* Practice areas */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <div className="section-label mb-3 justify-center">How We Help</div>
              <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold">Legal Services for {loc.shortName} Families</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRACTICE_LINKS.map((p) => (
                <a key={p.label + p.href} href={p.href} className="group flex items-center justify-between gap-2 bg-white border border-navy/10 px-4 py-4 hover:border-gold transition-colors">
                  <span className="font-body text-navy text-sm font-medium">{p.label}</span>
                  <ArrowRight className="w-4 h-4 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Areas served + map */}
        <section className="bg-white py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="section-label mb-3">Areas We Serve</div>
              <h2 className="font-display text-navy text-2xl lg:text-3xl font-bold mb-6">Communities around {loc.shortName}</h2>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                {loc.areasServed.map((a) => (
                  <li key={a} className="flex items-center gap-2 font-body text-navy/75 text-sm">
                    <Check className="w-4 h-4 text-gold flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy/5 border border-navy/10 overflow-hidden">
              <iframe
                src={mapSrc}
                title={`Map — ${loc.shortName}`}
                width="100%"
                height="360"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="block w-full border-0"
              />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="section-label mb-3">Frequently Asked Questions</div>
            <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold mb-8">{loc.shortName} Estate Law FAQ</h2>
            <div className="space-y-4">
              {loc.faqs.map((f) => (
                <details key={f.q} className="group bg-white border border-navy/10 p-5">
                  <summary className="flex items-start justify-between gap-3 cursor-pointer list-none font-display text-navy font-semibold">
                    {f.q}
                    <span className="text-gold text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="font-body text-navy/75 text-sm leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Booking */}
        <section id="book" className="bg-white py-16 lg:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="section-label mb-3 justify-center text-gold">Free Consultation</div>
            <h2 className="font-display text-navy text-3xl lg:text-4xl font-bold mb-3">Book Your {loc.shortName} Consultation</h2>
            <p className="font-body text-navy/70 text-base mb-8">
              Choose a time that works for you, or call <a href={`tel:${phoneHref}`} className="text-gold hover:underline">{phone}</a>.
            </p>
            <div className="bg-white border border-navy/10 shadow-xl overflow-hidden text-left">
              <CalendarEmbed height={640} title={`Morgan Legal Group — book your ${loc.shortName} consultation`} />
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
